let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (!audioCtx) {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
  }
  return audioCtx;
}

/** Quick satisfying ascending "ding" for correct answers. */
export function playCorrectSound() {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  // Bright two-note ascending chime
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.value = 880; // A5
  gain1.gain.setValueAtTime(0.3, now);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  osc1.connect(gain1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.15);

  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.value = 1319; // E6
  gain2.gain.setValueAtTime(0.01, now + 0.08);
  gain2.gain.linearRampToValueAtTime(0.3, now + 0.1);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
  osc2.connect(gain2).connect(ctx.destination);
  osc2.start(now + 0.08);
  osc2.stop(now + 0.3);
}

/** Gentle descending "bonk" for incorrect answers. */
export function playIncorrectSound() {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(300, now);
  osc.frequency.exponentialRampToValueAtTime(150, now + 0.2);
  gain.gain.setValueAtTime(0.15, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.25);
}

/**
 * Escalating streak sound — gets more intense with higher streaks.
 * streak 3: two quick high notes
 * streak 5: three notes with richer harmonics
 * streak 7+: four triumphant notes with dual oscillators
 */
export function playStreakSound(streak: number) {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  if (streak >= 7) {
    // Epic 4-note rising with dual oscillators
    const notes = [784, 988, 1175, 1568]; // G5, B5, D6, G6
    notes.forEach((freq, i) => {
      const start = now + i * 0.08;
      ['sine', 'triangle'].forEach((type) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type as OscillatorType;
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.01, start);
        gain.gain.linearRampToValueAtTime(type === 'sine' ? 0.25 : 0.12, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, start + 0.2);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.2);
      });
    });
  } else if (streak >= 5) {
    // 3-note rising
    const notes = [659, 880, 1175]; // E5, A5, D6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const start = now + i * 0.09;
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.01, start);
      gain.gain.linearRampToValueAtTime(0.28, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.18);
    });
  } else {
    // streak 3-4: two quick high notes
    const notes = [880, 1175]; // A5, D6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const start = now + i * 0.1;
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.01, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.15);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.15);
    });
  }
}

/** Subtle ticking urgency sound for timer warning zone. */
export function playTimerWarningSound() {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  // Two quick low ticks
  for (let i = 0; i < 2; i++) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = now + i * 0.12;
    osc.type = 'sine';
    osc.frequency.value = 440;
    gain.gain.setValueAtTime(0.2, start);
    gain.gain.exponentialRampToValueAtTime(0.01, start + 0.08);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.08);
  }
}

// ─── Background Music ───────────────────────────────────────────────

/**
 * Upbeat chiptune-style looping background music.
 * Returns a stop() function to kill it. Plays at low volume so
 * SFX cuts through on top.
 *
 * Structure (120 BPM, 0.5s per beat, 8-beat loop = 4s):
 *   Bass:    square wave, root notes
 *   Melody:  triangle wave, catchy 8-note phrase
 *   Hi-hat:  filtered noise on every beat
 */
export function startBackgroundMusic(): () => void {
  if (typeof window === 'undefined') return () => {};
  const ctx = getAudioContext();
  if (!ctx) return () => {};

  const BPM = 132;
  const beatSec = 60 / BPM;
  const loopBeats = 16;
  const loopDuration = loopBeats * beatSec;

  // Master gain — keep music quiet so SFX pops
  const master = ctx.createGain();
  master.gain.value = 0.12;
  master.connect(ctx.destination);

  // All scheduled oscillators so we can kill them
  const nodes: (OscillatorNode | AudioBufferSourceNode)[] = [];
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  // ── note helpers ──
  const bass = (freq: number, start: number, dur: number) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.35, start);
    g.gain.exponentialRampToValueAtTime(0.01, start + dur - 0.02);
    osc.connect(g).connect(master);
    osc.start(start);
    osc.stop(start + dur);
    nodes.push(osc);
  };

  const melody = (freq: number, start: number, dur: number) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.5, start);
    g.gain.exponentialRampToValueAtTime(0.01, start + dur - 0.02);
    osc.connect(g).connect(master);
    osc.start(start);
    osc.stop(start + dur);
    nodes.push(osc);
  };

  const hihat = (start: number) => {
    // Use a very short burst of high-freq oscillator as pseudo-noise
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.type = 'square';
    osc.frequency.value = 6000 + Math.random() * 2000;
    filter.type = 'highpass';
    filter.frequency.value = 7000;
    g.gain.setValueAtTime(0.3, start);
    g.gain.exponentialRampToValueAtTime(0.01, start + 0.06);
    osc.connect(filter).connect(g).connect(master);
    osc.start(start);
    osc.stop(start + 0.06);
    nodes.push(osc);
  };

  const kick = (start: number) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, start);
    osc.frequency.exponentialRampToValueAtTime(40, start + 0.12);
    g.gain.setValueAtTime(0.6, start);
    g.gain.exponentialRampToValueAtTime(0.01, start + 0.15);
    osc.connect(g).connect(master);
    osc.start(start);
    osc.stop(start + 0.15);
    nodes.push(osc);
  };

  // ── patterns (beat indices 0-15) ──
  // C major key, upbeat feel
  // Bass: half notes (2-beat holds)
  const bassPattern: [number, number][] = [
    // [beat, freq]  C3=131, D3=147, E3=165, F3=175, G3=196, A3=220
    [0, 131], [2, 131], [4, 175], [6, 196],
    [8, 131], [10, 165], [12, 175], [14, 196],
  ];

  // Melody: eighth-note resolution, catchy ascending/descending phrase
  // C4=262, D4=294, E4=330, F4=349, G4=392, A4=440, B4=494, C5=523
  const melodyPattern: [number, number][] = [
    [0, 523], [1, 392], [2, 440], [3, 523],
    [4, 349], [5, 392], [6, 440], [7, 349],
    [8, 523], [9, 392], [10, 330], [11, 392],
    [12, 440], [13, 523], [14, 659], [15, 523],
  ];

  // Hi-hat: every beat; kick: beats 0, 4, 8, 12
  const kickBeats = [0, 4, 8, 12];

  function scheduleLoop(loopStart: number) {
    if (stopped) return;

    // Bass
    for (const [beat, freq] of bassPattern) {
      bass(freq, loopStart + beat * beatSec, beatSec * 1.8);
    }

    // Melody
    for (const [beat, freq] of melodyPattern) {
      melody(freq, loopStart + beat * beatSec, beatSec * 0.85);
    }

    // Drums
    for (let b = 0; b < loopBeats; b++) {
      hihat(loopStart + b * beatSec + beatSec * 0.5); // off-beat hi-hat
      if (kickBeats.includes(b)) {
        kick(loopStart + b * beatSec);
      }
    }
  }

  // Schedule the first loop immediately
  let nextLoopStart = ctx.currentTime + 0.05;
  scheduleLoop(nextLoopStart);

  // Pre-schedule next loops ahead of time
  intervalId = setInterval(() => {
    if (stopped) return;
    // Schedule if we're within 2s of needing the next loop
    if (ctx.currentTime > nextLoopStart + loopDuration - 2) {
      nextLoopStart += loopDuration;
      scheduleLoop(nextLoopStart);
    }
  }, 1000);

  // Return stop function
  return () => {
    stopped = true;
    if (intervalId) clearInterval(intervalId);
    // Fade out master over 0.3s then disconnect
    const now = ctx.currentTime;
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0, now + 0.3);
    setTimeout(() => {
      nodes.forEach((n) => {
        try { n.stop(); } catch (_) { /* already stopped */ }
      });
      master.disconnect();
    }, 400);
  };
}

/** Rising frequency sweep — "whoosh" ignition feel for intro screen. */
export function playIgniteSound() {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(200, now);
  osc.frequency.exponentialRampToValueAtTime(800, now + 0.5);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(600, now);
  filter.frequency.exponentialRampToValueAtTime(2000, now + 0.5);

  gain.gain.setValueAtTime(0.2, now);
  gain.gain.linearRampToValueAtTime(0.25, now + 0.15);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

  osc.connect(filter).connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.55);
}

/** 4 rapid ascending notes (C5→E5→G5→C6) — power-up for level transitions. */
export function playPowerUpSound() {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = now + i * 0.1;

    osc.type = 'sine';
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(0.01, start);
    gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, start + 0.15);

    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.15);
  });
}

/** 5-note ascending C major arpeggio with dual oscillators — epic fanfare for celebration. */
export function playEpicFanfare() {
  if (typeof window === 'undefined') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const notes = [262, 330, 392, 523, 659]; // C4, E4, G4, C5, E5

  notes.forEach((freq, i) => {
    const isLast = i === notes.length - 1;
    const start = now + i * 0.15;
    const duration = isLast ? 0.6 : 0.25;

    // Sine oscillator
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.value = freq;
    gain1.gain.setValueAtTime(0.01, start);
    gain1.gain.linearRampToValueAtTime(0.2, start + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.01, start + duration);
    osc1.connect(gain1).connect(ctx.destination);
    osc1.start(start);
    osc1.stop(start + duration + 0.01);

    // Triangle oscillator for richer harmonics
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.value = freq;
    gain2.gain.setValueAtTime(0.01, start);
    gain2.gain.linearRampToValueAtTime(0.12, start + 0.02);
    gain2.gain.exponentialRampToValueAtTime(0.01, start + duration);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(start);
    osc2.stop(start + duration + 0.01);
  });
}
